import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { LiveSessionIconSvgComponent } from '../svg-sprites/general/live-session-icon-svg.component';
import { LessonsDBService } from '../services/lessons-db.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../store';
import { isAdmin, isCourseAdmin, isLoggedOut, selectUserCourses } from '../store/selectors';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'live-session-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LiveSessionIconSvgComponent],
  templateUrl: './live-session-bar.component.html',
  styleUrl: './live-session-bar.component.scss'
})
export class LiveSessionBarComponent {
  private lessonsDB = inject(LessonsDBService);
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private userPermissions = inject(UserPermissionsService);

  private readonly sessions = toSignal(this.lessonsDB.loadAllVisibleLiveSessionsAndWebinars());
  private readonly loggedOut = toSignal(this.store.select(isLoggedOut));
  private readonly admin = toSignal(this.store.select(isAdmin));
  private readonly courseAdmin = toSignal(this.store.select(isCourseAdmin));
  private readonly userCourses = toSignal(this.store.select(selectUserCourses));

  readonly isBannerVisible = signal(false);
  readonly currentLiveSession = signal<any>(null);

  constructor() {
    effect(() => {
      const sessions = this.sessions();
      const isLoggedOutVal = this.loggedOut();
      const isAdminVal = this.admin();
      const isCourseAdminVal = this.courseAdmin();
      const userCoursesVal = this.userCourses();

      if (!sessions || isLoggedOutVal) {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
        return;
      }

      const now = new Date();
      const liveSessions = sessions
        .filter(session => {
          const s = session.liveSession || session.webinar;
          if (!s?.sessionDate) return false;
          const start = s.sessionDate.toDate();
          const durationHours = s.sessionDuration?.hours || 1;
          const durationMinutes = s.sessionDuration?.minutes || 0;
          const end = new Date(start);
          end.setHours(end.getHours() + durationHours);
          end.setMinutes(end.getMinutes() + durationMinutes);
          return now >= start && now <= end;
        })
        .sort((a, b) => {
          const aStart = (a.liveSession || a.webinar).sessionDate.toDate().getTime();
          const bStart = (b.liveSession || b.webinar).sessionDate.toDate().getTime();
          return aStart - bStart;
        });

      if (!liveSessions.length) {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
        return;
      }

      const isStudent = !isAdminVal && !isCourseAdminVal &&
        !this.userPermissions.can(PermissionKeys.EditProduct);

      if (!isStudent) {
        this.currentLiveSession.set(liveSessions[0]);
        this.isBannerVisible.set(true);
        return;
      }

      const enrolledIds = new Set((userCoursesVal || []).map(c => c.id));
      const studentSessions = liveSessions.filter(s => enrolledIds.has(s.courseId));

      if (studentSessions.length) {
        this.currentLiveSession.set(studentSessions[0]);
        this.isBannerVisible.set(true);
      } else {
        this.isBannerVisible.set(false);
        this.currentLiveSession.set(null);
      }
    }, { allowSignalWrites: true });
  }

  joinLiveSession() {
    const session = this.currentLiveSession();
    if (session) {
      this.router.navigate([
        '/courses',
        session.courseUrl,
        session.sectionSeqNo || 1,
        'lessons',
        session.seqNo
      ]);
    }
  }

  closeLiveSolutionBanner() {
    this.isBannerVisible.set(false);
  }
}
